import React, { useState } from 'react'
import OrderDetailsNewContainer from './container'
import { useChangeOrderStatusMutation, useCollectOrderPaymentMutation, useCompleteOrderPaymentMutation, useGetOrderDetailsQuery, useSplitOrderBillMutation, usePaySplitBillMutation, useLazyGetPrintReceiptQuery, useRevertSplitPaymentMutation, useRemoveSplitBillMutation } from 'pages/orders/services'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOrder } from 'context/OrderContext'
import OrderDialogManager from 'components/Modal/OrderDialog/OrderDialogManager'
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog'
import CollectOrderPayment from 'components/Modal/CollectOrderPayment'
import OrderItemChefAssign from 'components/Modal/OrderItemChefAssign'
import OrderRiderAssign from 'components/Modal/OrderRiderAssign'
import SplitBillModal from 'components/Modal/SplitBillModal'
import { statusConfigs } from 'utils/constant'
import { useSocket } from 'context/SocketContext'
import { useEffect } from 'react'
import toast from 'react-hot-toast'

const OrderDeatilsNew = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isViewOnly = location?.state?.isViewOnly || false;
    const { openDialog, setCustomerData, customerData } = useOrder();

    const { data: orderDetails, isLoading, refetch } = useGetOrderDetailsQuery(location?.state?.orderId,
        { skip: !location?.state?.orderId });

    const [changeStatusApi, { isLoading: isUpdating }] = useChangeOrderStatusMutation();
    const [collectPaymentApi, { isLoading: isCollectingPayment }] = useCollectOrderPaymentMutation();
    const [completeOrderPaymentApi, { isLoading: isCompletingPayment }] = useCompleteOrderPaymentMutation();
    const [revertSplitPaymentApi, { isLoading: isRevertingSplit }] = useRevertSplitPaymentMutation();
    const [removeSplitBillApi, { isLoading: isRemovingSplit }] = useRemoveSplitBillMutation();
    const socket = useSocket();

    const [splitBillApi, { isLoading: isSplitting }] = useSplitOrderBillMutation();
    const [paySplitBillApi, { isLoading: isPayingSplit }] = usePaySplitBillMutation();
    const [triggerPrint] = useLazyGetPrintReceiptQuery();

    const [splitModalOpen, setSplitModalOpen] = useState(false);
    const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
    const [cancelSplitConfirmOpen, setCancelSplitConfirmOpen] = useState(false);

    const handleConfirmRevertPayment = async () => {
        try {
            const orderId = orderDetails?.data?.id;
            if (!orderId) return;
            const res = await revertSplitPaymentApi({ order_id: orderId }).unwrap();
            if (res?.status === 200 || res?.data) {
                toast.success("Payment reverted and split bill status updated to unpaid");
                setRefundConfirmOpen(false);
                await refetch();
                setSplitModalOpen(true);
            }
        } catch (error) {
            console.error("Error reverting split payment:", error);
            toast.error(error?.data?.message || "Failed to revert payment");
        }
    };

    const handleConfirmCancelSplitBill = async () => {
        try {
            const orderId = orderDetails?.data?.id;
            if (!orderId) return;
            const res = await removeSplitBillApi({ order_id: orderId }).unwrap();
            if (res?.status === 200 || res?.data) {
                toast.success("Split bill cancelled. Reverted to original bill.");
                setCancelSplitConfirmOpen(false);
                refetch();
            }
        } catch (error) {
            console.error("Error removing split bill:", error);
            toast.error(error?.data?.message || "Failed to cancel split bill");
        }
    };

    const prevItemCountRef = React.useRef(0);

    useEffect(() => {
        if (!socket || !location?.state?.orderId) return;

        const handleUpdate = (data) => {
            if (String(data.order_id) === String(location?.state?.orderId)) {
                console.log("Socket order update received in Order Details:", data);
                refetch();
            }
        };

        socket.on('order_status_update', handleUpdate);
        socket.on('order_items_update', handleUpdate);
        return () => {
            socket.off('order_status_update', handleUpdate);
            socket.off('order_items_update', handleUpdate);
        };
    }, [socket, location?.state?.orderId, refetch]);

    const lastHandledStateRef = React.useRef({
        orderId: null,
        itemQty: 0,
        orderTotal: 0,
        dismissed: false
    });

    // Check if unassigned items exist in an order that ALREADY HAS split bills and auto-open modal
    useEffect(() => {
        const orderData = orderDetails?.data;
        if (!orderData) return;

        const orderId = orderData.id;
        const splits = orderData.orderSplits || orderData.order_splits || [];
        const items = orderData.orderItems || orderData.items || orderData.order_items || [];

        // Reset tracking if order changed
        if (lastHandledStateRef.current.orderId !== orderId) {
            lastHandledStateRef.current = {
                orderId: orderId,
                itemQty: 0,
                orderTotal: 0,
                dismissed: false
            };
        }

        // ONLY trigger auto-open if the order ALREADY has generated split bills and new unassigned items/amount exist
        if (splits.length > 0 && items.length > 0) {
            const totalOrderItemQty = items.reduce((sum, item) => sum + parseInt(item.quantity || item.qty || 1, 10), 0);
            const orderTotal = parseFloat(orderData.total_amount || 0);

            const splitType = splits[0]?.split_type || 'item_wise';
            let unassignedDetected = false;

            if (splitType === 'item_wise') {
                let totalSplitItemQty = 0;
                splits.forEach(split => {
                    const splitItems = split.splitItems || split.orderSplitItems || split.items || split.order_split_items || [];
                    splitItems.forEach(si => {
                        totalSplitItemQty += parseInt(si.quantity || si.item_quantity || si.qty || 0, 10);
                    });
                });
                if (totalOrderItemQty > totalSplitItemQty) {
                    unassignedDetected = true;
                }
            } else {
                const totalSplitBillsSum = splits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
                if (orderTotal - totalSplitBillsSum > 0.5) {
                    unassignedDetected = true;
                }
            }

            const hasNewItemsOrAmount = totalOrderItemQty > lastHandledStateRef.current.itemQty || (orderTotal - lastHandledStateRef.current.orderTotal > 0.5);

            if (unassignedDetected && hasNewItemsOrAmount && !splitModalOpen && !lastHandledStateRef.current.dismissed) {
                lastHandledStateRef.current.itemQty = totalOrderItemQty;
                lastHandledStateRef.current.orderTotal = orderTotal;
                console.log("Unassigned new items detected in split order! Auto-opening SplitBillModal.");
                setSplitModalOpen(true);
            }
        }
    }, [orderDetails, splitModalOpen]);

    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState("cash");

    const [assignChefOpen, setAssignChefOpen] = useState(false);
    const [assignChefData, setAssignChefData] = useState(null);
    const [assignChefSelections, setAssignChefSelections] = useState({});
    const [pendingStatus, setPendingStatus] = useState(null);
    const [riderAssignOpen, setRiderAssignOpen] = useState(false);
    const [selectedRider, setSelectedRider] = useState(null);

    const handleBack = () => {
        navigate(-1)
    }

    const onEditCustomer = () => {
        const phone = orderDetails?.data?.customerVendor?.customerInfo?.phone != null
            ? String(orderDetails?.data?.customerVendor?.customerInfo?.phone)
            : (orderDetails?.data?.customer_mobile != null ? String(orderDetails?.data?.customer_mobile) : "");
        const name = orderDetails?.data?.customerVendor?.customer_full_name || orderDetails?.data?.customer_name || "";
        const countryCode = orderDetails?.data?.customerVendor?.customerInfo?.country_code != null
            ? String(orderDetails?.data?.customerVendor?.customerInfo?.country_code)
            : "44";

        const customerObj = {
            "id": orderDetails?.data?.customer_id,
            "phone_number": phone,
            "country_code": countryCode,
            "country_ISO": "gb",
            "customer_name": name,
            "persons": orderDetails?.data?.guest_count,
            "table": orderDetails?.data?.tableInfo?.id
        };
        const newTableData = orderDetails?.data?.tableInfo;
        const newData = { ...newTableData, order_id: orderDetails?.data?.id };
        setCustomerData(customerObj);

        const orderType = orderDetails?.data?.order_type;
        const typeMap = {
            take_away: 'takeAway',
            dine_in: 'dineIn',
            delivery: 'phoneOrder',
            table_booking: 'tableBooking'
        };
        const targetDialogType = typeMap[orderType] || 'dineIn';
        openDialog(targetDialogType, { table: newData, order_id: orderDetails?.data?.id, ...customerObj });
    }

    const handleStatusChange = async (status, extraBody = {}) => {
        const id = orderDetails?.data?.id;
        if (!id) return;
        setUpdatingStatus(true);
        const body = { order_status: status, ...extraBody }
        try {
            await changeStatusApi({ id, body })
            refetch();
        } catch (error) {
            console.log("Error in Changestatus API", error)
        } finally {
            setUpdatingStatus(false)
        }
    }

    const handleStatusWithRiderCheck = (order, newStatus) => {
        if (newStatus === "pickedup" && (order.order_type === "delivery" || order.order_type === "phone_order")) {
            setPendingStatus(newStatus);
            setRiderAssignOpen(true);
        } else {
            handleStatusWithChefCheck(order, newStatus);
        }
    }

    const handleRiderAssignSubmit = async () => {
        if (!selectedRider) return;
        try {
            await handleStatusChange("pickedup", { rider_id: selectedRider });
            setRiderAssignOpen(false);
            setPendingStatus(null);
            setSelectedRider(null);
            refetch();
        } catch (error) {
            console.error("Error in rider assign submit", error);
        }
    };

    // --- NEW: check items and open chef assign modal if needed ---
    const handleStatusWithChefCheck = (order, newStatus) => {
        // if (!order) {
        //     // fallback to internal handler if order not available
        //     return handleStatusChangeInternal(newStatus);
        // }

        // identify items that belong to stations where multiple chefs exist and primary_chef_id is not set
        const itemsWithoutChef = (order?.orderItems || []).filter(item =>
            item?.stationData?.chefs &&
            Array.isArray(item.stationData.chefs) &&
            item.stationData.chefs.length > 1 &&
            !item.primary_chef_id
        );

        if (itemsWithoutChef.length > 0 && (newStatus === "preparing" || newStatus === "ready_for_chef")) {
            // prepare modal data
            setAssignChefData({
                orderId: order.id,
                items: itemsWithoutChef
            });

            // default selection: first chef of each item's station
            const selections = {};
            itemsWithoutChef.forEach(item => {
                const chefs = item.stationData?.chefs || [];
                if (chefs.length > 0) selections[item.menu_item_id] = chefs[0].id;
            });

            setAssignChefSelections(selections);
            setPendingStatus(newStatus);
            setAssignChefOpen(true);
        } else {
            // no chef selection needed
            // handleStatusChangeInternal(newStatus);
            handleStatusChange(newStatus);
        }
    };

    // called by the child modal when a selection changes
    const handleAssignChefChange = (itemId, chefId) => {
        setAssignChefSelections(prev => ({
            ...prev,
            [itemId]: chefId,
        }));
    };

    // called when user confirms chef assignments + status change
    const handleAssignChefSubmit = async () => {
        if (!assignChefData || !pendingStatus) return;

        // build payload that your backend expects
        const payload = {
            order_id: assignChefData.orderId,
            order_status: pendingStatus,
            chef_assignments: Object.entries(assignChefSelections).map(([order_item_id, chef_id]) => ({
                order_item_id: Number(order_item_id),
                chef_id: Number(chef_id),
            })),
        };

        try {
            setUpdatingStatus(true);
            // call same changeStatusApi mutation — backend should handle assignments + status together
            await changeStatusApi({ id: assignChefData.orderId, body: payload });
            // close modal & reset
            setAssignChefOpen(false);
            setAssignChefData(null);
            setAssignChefSelections({});
            setPendingStatus(null);
            refetch();
        } catch (error) {
            console.error("Error submitting chef assignments:", error);
        } finally {
            setUpdatingStatus(false);
        }
    };

    // wrapper that'll be passed down to child components (they call with only status value)
    const onStatusChangeHandler = (status) => {
        const targetStatus = status === "complete" ? "completed" : status;
        if (["cancel", "reject"].includes(targetStatus)) {
            setConfirmAction(targetStatus);
            setConfirmOpen(true);
        } else if (targetStatus === "completed") {
            const alreadyPaid = orderDetails?.data?.paid_amount || 0;
            const totalAmount = orderDetails?.data?.total_amount || 0;
            if (orderDetails?.data?.payment_status !== "paid" || alreadyPaid < totalAmount) {
                setOpenPaymentModal(true);
            } else {
                setConfirmAction("completed");
                setConfirmOpen(true);
            }
        } else {
            handleStatusWithRiderCheck(orderDetails?.data, status);
        }
    };

    const handleCollectPayment = async (paymentMode) => {
        const alreadyPaid = orderDetails?.data?.paid_amount || 0;
        const totalAmount = orderDetails?.data?.total_amount || 0;
        const remainingAmount = totalAmount - alreadyPaid;

        const payload = {
            id: orderDetails?.data?.id,
            paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
            payment_mode: paymentMode,
        };

        try {
            const res = await collectPaymentApi(payload);
            if (res?.data?.status === 200) {
                setOpenPaymentModal(false);
                refetch();
            }
        } catch (err) {
            console.log("Error collecting payment", err);
        }
    };

    const handleCollectAndComplete = async (paymentMode) => {
        const alreadyPaid = orderDetails?.data?.paid_amount || 0;
        const totalAmount = orderDetails?.data?.total_amount || 0;
        const remainingAmount = totalAmount - alreadyPaid;

        const payload = {
            id: orderDetails?.data?.id,
            paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
            payment_mode: paymentMode,
        };

        try {
            const res = await completeOrderPaymentApi(payload);
            if (res?.data?.status === 200) {
                setOpenPaymentModal(false);
                navigate("/tables");
            }
        } catch (err) {
            console.log("Error in collect+complete", err);
        }
    };


    // const handleOpenDialog = (row, action) => {
    //     setPendingRow(row);
    //     setPendingAction(action);
    //     setDialogOpen(true);
    // };

    // const handleCloseDialog = () => {
    //     setDialogOpen(false);
    //     setPendingRow(null);
    //     setPendingAction(null);
    // };

    // const handleConfirm = async () => {
    //     if (pendingRow && pendingAction) {
    //         await handleStatusChange(pendingRow, pendingAction);
    //     }
    //     handleCloseDialog();
    // };



    const handleOpenSplitModal = () => {
        const orderSplits = orderDetails?.data?.orderSplits || [];
        if (orderSplits.length > 0) {
            const hasPaidSplit = orderSplits.some(split => split.payment_status === 'paid');
            if (hasPaidSplit) {
                setRefundConfirmOpen(true);
            } else {
                setSplitModalOpen(true);
            }
        } else {
            setSplitModalOpen(true);
        }
    };

    const handleCloseSplitModal = () => {
        const orderData = orderDetails?.data;
        if (orderData) {
            const items = orderData.orderItems || orderData.items || orderData.order_items || [];
            const totalOrderItemQty = items.reduce((sum, item) => sum + parseInt(item.quantity || item.qty || 1, 10), 0);
            const orderTotal = parseFloat(orderData.total_amount || 0);

            lastHandledStateRef.current = {
                orderId: orderData.id,
                itemQty: totalOrderItemQty,
                orderTotal: orderTotal,
                dismissed: true
            };
        }
        setSplitModalOpen(false);
    };

    const handleConfirmSplit = async (splitPayload) => {
        try {
            const orderId = orderDetails?.data?.id;
            if (!orderId) return;

            const items = orderDetails?.data?.orderItems || orderDetails?.data?.items || orderDetails?.data?.order_items || [];
            const totalOrderItemQty = items.reduce((sum, item) => sum + parseInt(item.quantity || item.qty || 1, 10), 0);
            const orderTotal = parseFloat(orderDetails?.data?.total_amount || 0);

            lastHandledStateRef.current = {
                orderId: orderId,
                itemQty: totalOrderItemQty,
                orderTotal: orderTotal,
                dismissed: true
            };

            const res = await splitBillApi({ id: orderId, body: splitPayload }).unwrap();
            toast.success("Bill split successfully");
            setSplitModalOpen(false);
            refetch();
        } catch (error) {
            console.error("Error splitting bill:", error);
            toast.error(error?.data?.message || "Failed to split bill");
        }
    };

    const handlePaySplitBill = async ({ order_split_id, payment_mode, total_amount }) => {
        try {
            const res = await paySplitBillApi({ order_split_id, payment_mode, total_amount }).unwrap();
            if (res?.status === 200 || res?.data) {
                toast.success("Split bill payment successful");
                refetch();
            }
        } catch (error) {
            console.error("Error paying split bill:", error);
            toast.error(error?.data?.message || "Failed to process split payment");
        }
    };

    const handlePrintSplitBill = async (splitRecord) => {
        try {
            const isCustomer = orderDetails?.data?.order_source === 'customer';
            const platformFeeValue = isCustomer ? parseFloat(orderDetails?.data?.platform_fee || 0) : 0;
            const platformFeePercent = isCustomer
                ? parseFloat(orderDetails?.data?.marketplace_fee_percentage ?? 0)
                : 0;
            const res = await triggerPrint({
                id: orderDetails?.data?.id,
                order_source: orderDetails?.data?.order_source || (orderDetails?.data?.customer_id ? 'customer' : 'pos'),
                platform_fee: platformFeeValue,
                platform_fee_percent: platformFeePercent
            }).unwrap();
            if (res?.data?.html) {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(res.data.html);
                    printWindow.document.close();
                    setTimeout(() => {
                        printWindow.print();
                        printWindow.close();
                    }, 500);
                }
            }
        } catch (error) {
            console.error("Failed to print split bill receipt:", error);
        }
    };

    return (
        <>
            <OrderDetailsNewContainer
                orderDetails={orderDetails?.data}
                handleBack={handleBack}
                onEditCustomer={onEditCustomer}
                onStatusChange={onStatusChangeHandler}
                updatingStatus={updatingStatus || isUpdating || isCompletingPayment}
                isViewOnly={isViewOnly}
                refetch={refetch}
                isLoading={isLoading}
                onOpenSplitModal={handleOpenSplitModal}
                onPaySplitBill={handlePaySplitBill}
                onPrintSplitBill={handlePrintSplitBill}
                isPayingSplit={isPayingSplit}
                onRemoveSplitBill={() => setCancelSplitConfirmOpen(true)}
                isRemovingSplit={isRemovingSplit}
            />
            <OrderDialogManager />

            <SplitBillModal
                open={splitModalOpen}
                onClose={handleCloseSplitModal}
                orderDetails={orderDetails?.data}
                onConfirmSplit={handleConfirmSplit}
                isLoading={isSplitting}
                disableClose={(() => {
                    const orderData = orderDetails?.data;
                    if (!orderData) return false;
                    const splits = orderData.orderSplits || orderData.order_splits || [];
                    const items = orderData.orderItems || orderData.items || orderData.order_items || [];
                    if (splits.length === 0 || items.length === 0) return false;
                    
                    const totalOrderItemQty = items.reduce((sum, item) => sum + parseInt(item.quantity || item.qty || 1, 10), 0);
                    const orderTotal = parseFloat(orderData.total_amount || 0);
                    const splitType = splits[0]?.split_type || 'item_wise';

                    if (splitType === 'item_wise') {
                        let totalSplitItemQty = 0;
                        splits.forEach(split => {
                            const splitItems = split.splitItems || split.orderSplitItems || split.items || split.order_split_items || [];
                            splitItems.forEach(si => {
                                totalSplitItemQty += parseInt(si.quantity || si.item_quantity || si.qty || 0, 10);
                            });
                        });
                        return totalOrderItemQty > totalSplitItemQty;
                    } else {
                        const totalSplitBillsSum = splits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
                        return (orderTotal - totalSplitBillsSum > 0.5);
                    }
                })()}
            />

            <ConfirmationDialog
                open={refundConfirmOpen}
                handleClose={() => setRefundConfirmOpen(false)}
                handleConfirm={handleConfirmRevertPayment}
                isLoading={isRevertingSplit}
                btnText="Yes, Revert & Refund"
                infoText="You have already split the bill for this order and one or more split bills are paid. Reverting will refund those payments and mark all split bills as unpaid so you can re-split or edit."
                action="Revert Payment & Refund"
            />

            <ConfirmationDialog
                open={cancelSplitConfirmOpen}
                handleClose={() => setCancelSplitConfirmOpen(false)}
                handleConfirm={handleConfirmCancelSplitBill}
                isLoading={isRemovingSplit}
                btnText="Yes, Cancel Split"
                infoText="Are you sure you want to cancel the split bill and revert to the original bill?"
                action="Cancel Split Bill"
            />

            {/* <ConfirmationDialog
      open={confirmOpen}
      handleClose={() => setConfirmOpen(false)}
      handleConfirm={async () => {
            const alreadyPaid = orderDetails?.data?.paid_amount || 0;
            const totalAmount = orderDetails?.data?.total_amount || 0;
            const remainingAmount = totalAmount - alreadyPaid;

            const payload = {
                id: orderDetails?.data?.id,
                paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
                payment_mode: "cash", // or last used mode
            };

            try {
                const res = await completeOrderPaymentApi(payload);
                if (res?.data?.status === 200) {
                    navigate("/tables");
                }
            } catch (err) {
                console.log("Error completing order", err);
            } finally {
                setConfirmOpen(false);
            }
        }}
      btnText="Complete Order"
      action="Complete"
      infoText="Are you sure you want to complete this order?"
    /> */}

            {/* ✅ Confirm dialog for cancel/reject/complete */}
            <ConfirmationDialog
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                handleConfirm={async () => {
                    try {
                        if (confirmAction === "completed") {
                            const payload = {
                                id: orderDetails?.data?.id,
                                paid_amount: orderDetails?.data?.total_amount,
                                payment_mode: "cash",
                            };
                            await completeOrderPaymentApi(payload);
                            navigate("/tables");
                        } else if (confirmAction === "pickedup") {
                            handleStatusWithRiderCheck(orderDetails?.data, confirmAction);
                            return;
                        } else {
                            handleStatusWithChefCheck(orderDetails?.data, confirmAction);
                            refetch();
                        }
                    } catch (err) {
                        console.log("Error completing confirm action", err);
                    } finally {
                        setConfirmOpen(false);
                    }
                }}
                btnText="Confirm"
                infoText={`Are you sure you want to change status to ${confirmAction === 'completed' && orderDetails?.data?.order_type === 'delivery' && (orderDetails?.data?.payment_status === "paid" || (orderDetails?.data?.paid_amount && orderDetails?.data?.total_amount && parseFloat(orderDetails?.data?.paid_amount) >= parseFloat(orderDetails?.data?.total_amount)))
                    ? 'delivered & completed'
                    : statusConfigs[confirmAction]?.label?.toLowerCase()
                    }?`}
                action={
                    confirmAction === 'completed' && orderDetails?.data?.order_type === 'delivery' && (orderDetails?.data?.payment_status === "paid" || (orderDetails?.data?.paid_amount && orderDetails?.data?.total_amount && parseFloat(orderDetails?.data?.paid_amount) >= parseFloat(orderDetails?.data?.total_amount)))
                        ? 'Delivered & Completed'
                        : statusConfigs[confirmAction]?.label || ''
                }
                isLoading={updatingStatus || isUpdating || isCompletingPayment}
            />


            <CollectOrderPayment
                open={openPaymentModal}
                onClose={() => setOpenPaymentModal(false)}
                orderDetails={orderDetails?.data}
                onCollectPayment={handleCollectPayment}
                onCollectAndComplete={handleCollectAndComplete}
                isCollecting={isCollectingPayment}
                isCompleting={isCompletingPayment}
            />

            <OrderItemChefAssign
                open={assignChefOpen}
                onClose={() => {
                    setAssignChefOpen(false);
                    setAssignChefData(null);
                    setAssignChefSelections({});
                    setPendingStatus(null);
                }}
                orderItems={assignChefData?.items || []}
                selections={assignChefSelections}
                onChangeChef={handleAssignChefChange}
                onSubmit={handleAssignChefSubmit}
                loading={isUpdating || updatingStatus}
            />

            <OrderRiderAssign
                open={riderAssignOpen}
                onClose={() => {
                    setRiderAssignOpen(false);
                    setPendingStatus(null);
                    setSelectedRider(null);
                }}
                onSubmit={handleRiderAssignSubmit}
                loading={isUpdating || updatingStatus}
                selectedRider={selectedRider}
                setSelectedRider={setSelectedRider}
                orderDetails={orderDetails?.data}
            />
        </>
    )
}

export default OrderDeatilsNew
